import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStrings } from '@/i18n/use-strings';
import { useUserStore } from '@/store/user';

/**
 * The guard in front of the only destructive action in the app.
 *
 * Cancel is the filled primary and comes first in the DOM, so it is what Radix
 * lands focus on and the obvious way out; clearing everything is a red
 * *outlined* secondary. Red is reserved for exactly this (ticket 09 §11) —
 * every other chrome action is blue. Below `sm` the footer reverses, which puts
 * Cancel nearest the thumb and the destructive action a deliberate reach away.
 *
 * The outlined red is `destructive-outline` rather than an `outline` with
 * overrides on it. The overrides used to reach only the ground, so `outline`'s
 * own `hover:text-accent-foreground` still fired and painted a near-white
 * label on the pale pink hover (ticket 23 §3). A variant owns both.
 */
export function StartOverDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { header } = useStrings();

  const resetAll = useUserStore((state) => state.resetAll);
  const navigate = useNavigate();

  const confirm = () => {
    resetAll();
    onOpenChange(false);

    void navigate('/', { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The surface, the radius, the display face and the muted body copy are
          `DialogContent`'s own now (ticket 23 §4); this screen only says what
          goes in it. */}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{header.start_over_title}</DialogTitle>
          <DialogDescription>{header.start_over_body}</DialogDescription>
        </DialogHeader>

        {/* `DialogFooter` brings its own hairline and its own bottom padding
            now, so this only says the two actions stack on a desktop too. */}
        <DialogFooter className="sm:flex-col">
          <Button variant="brand" size="xl" onClick={() => onOpenChange(false)}>
            {header.start_over_cancel}
          </Button>
          <Button variant="destructive-outline" size="xl" onClick={confirm}>
            {header.start_over_confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
